import React from 'react';

// Helper function to get formatted service names from appointment
const getServiceNames = (appointment) => {
  // Handle new format: service_names array
  if (appointment.service_names && Array.isArray(appointment.service_names)) {
    return appointment.service_names.join(', ');
  }
  // Handle old format: single service_name string
  if (appointment.service_name) {
    return appointment.service_name;
  }
  // Fallback to service field
  if (appointment.service) {
    return appointment.service;
  }
  return 'N/A';
};

/**
 * UserInfoModal Component
 * Displays detailed information about a user (patient or service worker)
 * 
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - user: User object containing user details
 * - onClose: Callback function to close modal
 * - formatDateTime: Function to format dates/times
 * - getStatusBadgeClass: Function to get CSS class for status badge
 * - getUserSchedule: Function that takes (userId, userType) and returns appointments array
 * - showSchedule: Boolean to show/hide schedule section (optional, defaults to true)
 */
export default function UserInfoModal({
    isOpen,
    user,
    onClose,
    formatDateTime,
    getStatusBadgeClass,
    getUserSchedule,
    showSchedule = true
}) {
    if (!isOpen || !user) {
        return null;
    }

    const userDisplayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A';
    const userType = user.type || (user.psw_id ? 'service_worker' : 'patient');
    const userTypeLabel = userType === 'service_worker' ? 'Service Worker' : 'Patient';
    const userId = user.psw_id || user.id || user.user_id;
    const schedule = showSchedule ? getUserSchedule(userId, userType) : [];

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div 
                className="modal-content appointment-details-modal" 
                onClick={(e) => e.stopPropagation()} 
                style={{ maxWidth: '800px' }}
            >
                <div className="modal-header">
                    <h2>{userTypeLabel} Information</h2>
                    <button 
                        className="modal-close" 
                        onClick={onClose} 
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <div className="appointment-details-content">
                    {/* Name */}
                    <div className="detail-row">
                        <span className="detail-label">Name:</span>
                        <span className="detail-value">{userDisplayName}</span>
                    </div>

                    {/* Username */}
                    {user.username && (
                        <div className="detail-row">
                            <span className="detail-label">Username:</span>
                            <span className="detail-value">{user.username}</span>
                        </div>
                    )}

                    {/* Email */}
                    {user.email && (
                        <div className="detail-row">
                            <span className="detail-label">Email:</span>
                            <span className="detail-value">{user.email}</span>
                        </div>
                    )}

                    {/* Phone */}
                    {user.phone_number && (
                        <div className="detail-row">
                            <span className="detail-label">Phone:</span>
                            <span className="detail-value">{user.phone_number}</span>
                        </div>
                    )}

                    {/* Date of Birth */}
                    {user.date_of_birth && (
                        <div className="detail-row">
                            <span className="detail-label">Date of Birth:</span>
                            <span className="detail-value">{user.date_of_birth}</span>
                        </div>
                    )}

                    {/* Sex */}
                    {user.sex && (
                        <div className="detail-row">
                            <span className="detail-label">Sex:</span>
                            <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                                {user.sex}
                            </span>
                        </div>
                    )}

                    {/* Address */}
                    {user.address && (
                        <div className="detail-row">
                            <span className="detail-label">Address:</span>
                            <span className="detail-value">{user.address}</span>
                        </div>
                    )}

                    {/* Account Status */}
                    {user.is_active !== undefined && (
                        <div className="detail-row">
                            <span className="detail-label">Account Status:</span>
                            <span 
                                className="detail-value" 
                                style={{
                                    color: user.is_active ? '#22c55e' : '#ef4444',
                                    fontWeight: '600'
                                }}
                            >
                                {user.is_active ? '✓ Active' : '✗ Inactive'}
                            </span>
                        </div>
                    )}

                    {/* Last Updated */}
                    {user.updated_at && (
                        <div className="detail-row">
                            <span className="detail-label">Last Updated:</span>
                            <span className="detail-value">{formatDate(user.updated_at)}</span>
                        </div>
                    )}

                    {/* Schedule Section */}
                    {showSchedule && (
                        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                            <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Schedule</h3>
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {schedule.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f8f9fa' }}>
                                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Time</th>
                                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Service</th>
                                                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedule.map((apt, idx) => {
                                                const { date, time } = formatDateTime(apt.start_date);
                                                return (
                                                    <tr key={apt.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '8px' }}>{date}</td>
                                                        <td style={{ padding: '8px' }}>{time}</td>
                                                        <td style={{ padding: '8px' }}>{getServiceNames(apt)}</td>
                                                        <td style={{ padding: '8px' }}>
                                                            <span className={getStatusBadgeClass(apt.status)}>
                                                                {apt.status || 'N/A'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
                                        No appointments scheduled
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button 
                        type="button" 
                        className="modal-button-cancel" 
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
